import React, { useState, useEffect } from "react";
import { showSuccess, showError } from "toast/toast";
import { useNavigate } from "react-router-dom";
import { getImageSrc, usePresignedMap } from "components/Tableimageshelpers";

import { getAuthCookie } from "../utils/authCookies";
export type ReviewFormData = {
  rating: number;
  title: string;
  body: string;
  name: string;
  email?: string;
  recommend: boolean;
};

export interface ReviewModalProps {
  show: boolean;
  vendor: { name: string; id?: string; logo?: string } | null;        // only what we render
  onClose: () => void;
  onSubmit?: (data: ReviewFormData) => void | Promise<void>; // optional; if not passed we’ll just close
  stacked?: boolean;                        // when opened above another modal
  reviewType?: 'vendor' | 'product';       // to determine which API to call
  profileId?: string;                      // vendor profile ID for vendor reviews

  // ⬇️ NEW for product reviews
  productId?: string;
  productVariantId?: string;
  logoUrlMap?: Record<string, string>; // ⬅️ new prop
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  show,
  vendor,
  onClose,
  onSubmit,
  stacked = false,
  reviewType = 'vendor',
  profileId,
  // ⬇️ NEW
  productId,
  productVariantId,
  logoUrlMap
}) => {
  const [reviewRating, setReviewRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [recommend, setRecommend] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const navigate = useNavigate();

  const ratingLabel = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];

  useEffect(() => {
    if (!show || !vendor) return;

    const fetchProfile = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        console.log("Profile data :", data);

        if (data) {
          setName(`${data.firstName} ${data.lastName}`)
          setEmail(data.email)
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching license data:", error);
      }
    };

    fetchProfile();
  }, [userId, token, show, vendor]);

  if (!show || !vendor) return null;

  const handleSubmit = async () => {
    // Validation
    if (!reviewRating || !title.trim() || !body.trim() || !name.trim()) {
      showError("Please fill in all required fields");
      return;
    }

    if (!token) {
      showError("Authentication required");
      return;
    }

    setIsSubmitting(true);

    try {
      if (reviewType === 'vendor' && profileId) {
        // Vendor review API call
        const vendorReviewPayload = {
          profile_id: profileId,
          user_id: userId,
          user_name: name.trim(),
          email: email.trim() || undefined,
          rating: reviewRating,
          review_title: title.trim(),
          review_comment: body.trim(),
          recommend_vendor: recommend,
          status: "active"
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}/marketplace-reviews`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(vendorReviewPayload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData?.message || `Failed to submit review (${response.status})`);
        }

        showSuccess("Review submitted successfully!");
        navigate(0)
      } else if (reviewType === 'product' && productId) {
        const productReviewPayload = {
          product_id: productId,
          product_variant_id: productVariantId || undefined,
          user_id: userId,
          user_name: name.trim(),
          email: email.trim() || undefined,
          rating: reviewRating,                 // 1..5 from UI
          review_title: title.trim(),
          review_comment: body.trim(),
        };
        const response = await fetch(`${process.env.REACT_APP_API_URL}/product-reviews`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(productReviewPayload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData?.message || `Failed to submit review (${response.status})`);
        }

        showSuccess("Review submitted successfully!");
      } else {
        showError("Invalid review type");
        return;
      }

      // Call custom onSubmit if provided
      if (onSubmit) {
        const payload: ReviewFormData = {
          rating: reviewRating,
          title,
          body,
          name,
          email: email || undefined,
          recommend,
        };
        onSubmit(payload);
      }

      onClose();
    } catch (error: any) {
      console.error("Review submission error:", error);
      showError(error.message || "Failed to submit review");
    } finally {
      setIsSubmitting(false);
    }
  };
  const logoSrc = getImageSrc(vendor?.logo, logoUrlMap ?? {});
  const overlayStyle = stacked ? { zIndex: 1055 } : undefined;
  const modalStyle = stacked ? { zIndex: 1060 } : undefined;

  return (
    <>
      <div
        id="reviewModalOverlay"
        className={`review-modal-overlay ${show ? "active" : ""}`}
        onClick={onClose}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close review modal"
        style={overlayStyle}
      />
      <div
        id="reviewModal"
        className={`review-modal ${show ? "active" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Write a review"
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="review-modal-header">
          <h3 className="review-modal-title" id="reviewModalTitle">
            Write a Review for {vendor.name}
          </h3>
          <button
            type="button"
            className="review-modal-close"
            id="reviewModalCloseBtn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="review-modal-content">
          <div className="review-vendor-info" id="reviewVendorInfo">
            <div className="review-vendor-logo">
              {vendor?.logo ? (
                <img
                  src={logoSrc}
                  alt={vendor?.name || "Vendor"}
                  style={{ width: 70, height: 60, objectFit: "contain", borderRadius: 8 }}
                />
              ) : (
                <span>🏷️</span>
              )}
            </div>
            <div className="review-vendor-details ms-3">
              <h4>{vendor.name}</h4>
              <span>{reviewType === "product" ? "Product Review" : "Vendor Review"}</span>
            </div>
          </div>

          <form
            id="reviewForm"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="review-form-group">
              <label className="review-form-label">
                Overall Rating <span className="required">*</span>
              </label>
              <div className="star-rating">
                <div className="star-rating-stars" id="starRating">
                  {[1, 2, 3, 4, 5].map((i) => {
                    const active = (hoverRating || reviewRating) >= i;
                    return (
                      <span
                        key={i}
                        className={`star-rating-star ${active ? "active" : ""}`}
                        data-rating={i}
                        role="button"
                        tabIndex={0}
                        onClick={() => setReviewRating(i)}
                        onMouseEnter={() => setHoverRating(i)}
                        onMouseLeave={() => setHoverRating(0)}
                        onKeyDown={(e) => e.key === "Enter" && setReviewRating(i)}
                        aria-label={`${i} star${i > 1 ? "s" : ""}`}
                      >
                        ★
                      </span>
                    );
                  })}
                </div>
                <span className="star-rating-text" id="ratingText">
                  {(hoverRating || reviewRating) || 0} out of 5 stars
                  {(hoverRating || reviewRating) ? ` - ${ratingLabel[hoverRating || reviewRating]}` : ""}
                </span>
              </div>
            </div>

            <div className="review-form-group">
              <label className="review-form-label">
                Review Title <span className="required">*</span>
              </label>
              <input
                type="text"
                className="review-form-input"
                id="reviewTitle"
                placeholder="Summarize your experience"
                maxLength={100}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <div className="review-char-count">
                <span id="titleCharCount">{title.length}</span>/100 characters
              </div>
            </div>

            <div className="review-form-group">
              <label className="review-form-label">
                Your Review <span className="required">*</span>
              </label>
              <textarea
                className="review-form-textarea"
                id="reviewText"
                placeholder="Share your experience with this vendor. What did you like or dislike? How was the quality, service, or value?"
                maxLength={1000}
                rows={5}
                style={{ resize: "none" }}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
              />
              <div className="review-char-count">
                <span id="reviewCharCount">{body.length}</span>/1000 characters
              </div>
            </div>

            <div className="review-form-group">
              <label className="review-recommend">
                <input
                  type="checkbox"
                  id="reviewRecommend"
                  checked={recommend}
                  onChange={(e) => setRecommend(e.target.checked)}
                />
                <span>I would recommend this vendor to others</span>
              </label>
            </div>
          </form>
        </div>

        <div className="review-modal-footer">
          <button type="button" className="btn-review-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-review-submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Submitting...
              </>
            ) : (
              "Submit Review"
            )}
          </button>
        </div>
      </div>
    </>
  );
};
